import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import api from "services/api";
import { useTranslation } from "react-i18next";

export default function ReunionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    titre: "",
    date: "",
    heure_debut: "",
    heure_fin: "",
    lieu: "",
    ordre_du_jour: "",
    statut: "planifiee",
  });
  const [enseignants, setEnseignants] = useState([]);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/enseignants").then(({ data }) => setEnseignants(data));
    if (isEdit) {
      api.get(`/reunions/${id}`).then(({ data }) => {
        setForm({
          titre: data.titre || "",
          date: data.date || "",
          heure_debut: data.heure_debut || "",
          heure_fin: data.heure_fin || "",
          lieu: data.lieu || "",
          ordre_du_jour: data.ordre_du_jour || "",
          statut: data.statut || "planifiee",
        });
        const initSel = {};
        (data.convocations || []).forEach((c) => { initSel[c.user_id] = true; });
        setSelected(initSel);
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleAll = (val) => {
    const next = {};
    enseignants.forEach((e) => { next[e.id] = val; });
    setSelected(next);
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.titre || !form.date || !form.heure_debut) {
      setError(t("reunions.form.erreurChamps"));
      return;
    }
    const enseignant_ids = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => parseInt(k, 10));

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/reunions/${id}`, form);
        navigate(`/reunions/${id}`);
      } else {
        const { data } = await api.post("/reunions", { ...form, enseignant_ids });
        navigate(`/reunions/${data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || t("commun.erreurEnregistrement"));
      setSaving(false);
    }
  };

  const nbSelected = Object.values(selected).filter(Boolean).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <MDButton variant="text" color="secondary" size="small" onClick={() => navigate("/reunions")}>
            <Icon>arrow_back</Icon> {t("commun.retour")}
          </MDButton>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            {isEdit ? t("reunions.form.titreModifier") : t("reunions.nouvelle")}
          </MDTypography>
        </MDBox>

        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h6" mb={2}>{t("reunions.informations")}</MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label={t("champs.titre")} name="titre" value={form.titre} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="date" label={t("champs.date")} name="date" value={form.date}
                onChange={handleChange} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="time" label={t("reunions.form.heureDebut")} name="heure_debut" value={form.heure_debut}
                onChange={handleChange} InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="time" label={t("reunions.form.heureFin")} name="heure_fin" value={form.heure_fin}
                onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={t("reunions.form.lieu")} name="lieu" value={form.lieu} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label={t("champs.ordreDuJour")} name="ordre_du_jour"
                value={form.ordre_du_jour} onChange={handleChange} />
            </Grid>
            {isEdit && (
              <Grid item xs={12} md={4}>
                <TextField fullWidth select SelectProps={{ native: true }} label={t("champs.statut")}
                  name="statut" value={form.statut} onChange={handleChange}>
                  <option value="planifiee">{t("valeurs.statutReunion.planifiee")}</option>
                  <option value="terminee">{t("valeurs.statutReunion.terminee")}</option>
                  <option value="annulee">{t("valeurs.statutReunion.annulee")}</option>
                </TextField>
              </Grid>
            )}
          </Grid>
        </Card>

        {!isEdit && (
          <Card sx={{ p: 3, mb: 3 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDBox>
                <MDTypography variant="h6">{t("reunions.form.convoquer")}</MDTypography>
                <MDTypography variant="caption" color="text">
                  {t("cours.form.enseignantsSelectionnes", { count: nbSelected })}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1}>
                <MDButton size="small" variant="outlined" color="info" onClick={() => toggleAll(true)}>{t("commun.toutCocher")}</MDButton>
                <MDButton size="small" variant="outlined" color="secondary" onClick={() => toggleAll(false)}>{t("commun.toutDecocher")}</MDButton>
              </MDBox>
            </MDBox>
            <MDBox sx={{
              maxHeight: 300, overflowY: "auto",
              border: "1px solid #e0e0e0", borderRadius: "8px", background: "#fff", p: 1,
            }}>
              {enseignants.map((e) => (
                <label key={e.id} style={{
                  display: "flex", alignItems: "center", gap: 10, marginBottom: 4,
                  cursor: "pointer", fontSize: 14, color: "#344767",
                  padding: "8px 10px", borderRadius: "6px",
                  background: selected[e.id] ? "#e3f2fd" : "transparent",
                }}>
                  <input type="checkbox"
                    checked={!!selected[e.id]}
                    onChange={(ev) => setSelected((prev) => ({ ...prev, [e.id]: ev.target.checked }))}
                    style={{ cursor: "pointer", width: 16, height: 16 }} />
                  <span style={{ flex: 1 }}>
                    <strong>{e.prenom} {e.nom}</strong>
                    {e.email && <span style={{ color: "#9e9e9e", marginLeft: 8, fontSize: 12 }}>— <bdi dir="ltr">{e.email}</bdi></span>}
                  </span>
                  {!e.email && (
                    <span style={{ background: "#ffebee", color: "#c62828", fontSize: 10, padding: "2px 6px", borderRadius: 4 }}>
                      {t("commun.sansEmail")}
                    </span>
                  )}
                </label>
              ))}
            </MDBox>
          </Card>
        )}

        {error && (
          <MDBox p={2} mb={2} sx={{ background: "#ffebee", borderRadius: "8px", color: "#c62828", fontSize: 13 }}>
            ⚠️ {error}
          </MDBox>
        )}

        <MDBox display="flex" gap={2} justifyContent="flex-end">
          <MDButton variant="outlined" color="secondary" onClick={() => navigate("/reunions")}>{t("commun.annuler")}</MDButton>
          <MDButton onClick={handleSubmit} disabled={saving}
            sx={{ background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 700 }}>
            {saving ? t("commun.enregistrement") : isEdit ? t("commun.enregistrer") : t("reunions.form.creer")}
          </MDButton>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
